import { useId, useLayoutEffect, useRef, useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { Button, Icon } from '@susu/ui/components'

import {
  NBA_FEEDBACK_NOTES_MAX_LENGTH,
  type NbaFeedbackScore,
} from '../api/nbaCardApi'

interface NbaFeedbackSheetProps {
  rating: NbaFeedbackScore
  /** Fires once per sheet: the typed note on Send, `''` on Skip / close. */
  onFinish: (notes: string) => void
}

/**
 * Bottom sheet that collects the optional note after a thumbs up/down on the
 * Next Best Action card (RP-6164). Dismissing it any way — close icon,
 * backdrop, Escape or Skip — still counts as a rating with an empty note.
 */
export default function NbaFeedbackSheet({
  rating,
  onFinish,
}: Readonly<NbaFeedbackSheetProps>) {
  const { t } = useI18N()
  const [notes, setNotes] = useState('')
  const titleId = useId()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const noteRef = useRef<HTMLTextAreaElement>(null)
  const isPositive = rating === 'good'

  // `showModal()` puts the sheet in the top layer, traps focus inside it and
  // turns Escape into a `cancel` event. Optional-called because jsdom does not
  // implement it. The note is focused afterwards because `showModal()` moves
  // focus to the dialog's first focusable element; a layout effect keeps the
  // focus() inside the thumb tap's task, which iOS needs to raise the keyboard.
  useLayoutEffect(() => {
    dialogRef.current?.showModal?.()
    noteRef.current?.focus()
  }, [])

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      className="m-0 h-dvh max-h-none w-full max-w-none border-0 bg-transparent p-0 backdrop:bg-black-alpha-500"
      data-testid="nba-feedback-sheet"
      onCancel={(event) => {
        event.preventDefault()
        onFinish('')
      }}
    >
      <div className="flex h-full flex-col">
        {/* Tap target for the dimmed area above the panel — closing that way
            counts the same as the close icon. */}
        <button
          type="button"
          tabIndex={-1}
          aria-label={t('nbaCard.feedback.close')}
          className="min-h-0 w-full flex-1 cursor-default"
          data-testid="nba-feedback-sheet-backdrop"
          onClick={() => onFinish('')}
        />
        <div className="mx-auto flex w-full max-w-[600px] flex-col gap-16 rounded-t-lg bg-surface px-16 pt-16 pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-xl">
          <div className="flex items-start justify-between gap-8">
            <div className="flex flex-col gap-4 pt-10">
              <h2
                id={titleId}
                className="text-xl leading-xl font-medium -tracking-[0.015em] text-text"
                data-testid="nba-feedback-sheet-title"
              >
                {isPositive
                  ? t('nbaCard.feedback.positiveTitle')
                  : t('nbaCard.feedback.negativeTitle')}
              </h2>
              <p className="text-sm leading-sm font-light text-text-secondary">
                {t('nbaCard.feedback.subcopy')}
              </p>
            </div>
            <button
              type="button"
              aria-label={t('nbaCard.feedback.close')}
              className="-mr-10 flex h-44 w-44 shrink-0 items-center justify-center rounded-full hover:bg-surface-hover"
              data-testid="nba-feedback-sheet-close"
              onClick={() => onFinish('')}
            >
              <Icon name="close" size={24} color="text" />
            </button>
          </div>
          <textarea
            ref={noteRef}
            aria-label={t('nbaCard.feedback.noteLabel')}
            className="min-h-120 w-full resize-none rounded-md border border-border bg-surface p-12 text-base leading-base font-light text-text placeholder:text-text-placeholder focus:border-border-focus focus:outline-none"
            data-testid="nba-feedback-sheet-note"
            maxLength={NBA_FEEDBACK_NOTES_MAX_LENGTH}
            placeholder={
              isPositive
                ? t('nbaCard.feedback.positivePlaceholder')
                : t('nbaCard.feedback.negativePlaceholder')
            }
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          <div className="flex gap-8">
            <Button
              type="button"
              variant="outlined"
              className="flex-1"
              data-testid="nba-feedback-sheet-skip"
              onClick={() => onFinish('')}
            >
              {t('nbaCard.feedback.skip')}
            </Button>
            <Button
              type="button"
              className="flex-1"
              data-testid="nba-feedback-sheet-send"
              onClick={() => onFinish(notes)}
            >
              {t('nbaCard.feedback.send')}
            </Button>
          </div>
        </div>
      </div>
    </dialog>
  )
}
